import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    TextField,
    Button,
    MenuItem,
    IconButton,
    CircularProgress,
    Snackbar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Autocomplete,
    Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import {
    getFirestore,
    collection,
    getDocs,
    query,
    orderBy,
    setDoc,
    doc,
    getDoc,
    runTransaction
} from 'firebase/firestore';

const firestore = getFirestore();
const tiposRemito = ['Ingreso', 'Egreso', 'Pase de materiales'];

function AddRemito() {
    const [loading, setLoading] = useState(false);
    const [cuadrillas, setCuadrillas] = useState([]);
    const [obras, setObras] = useState([]);
    const [materiales, setMateriales] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [formData, setFormData] = useState({
        tipoRemito: 'Ingreso',
        fecha: moment(),
        cuadrillaOrigen: '',
        cuadrillaDestino: '',
        obraOrigen: '',
        obraDestino: '',
        observaciones: ''
    });

    const [isOtroCuadrillaOrigen, setIsOtroCuadrillaOrigen] = useState(false);
    const [cuadrillaOrigenCustom, setCuadrillaOrigenCustom] = useState('');

    const [materialesSeleccionados, setMaterialesSeleccionados] = useState([]);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            await Promise.all([
                fetchCuadrillas(),
                fetchObras(),
                fetchMateriales()
            ]);
        } catch (error) {
            console.error('Error cargando datos:', error);
            showSnackbar('Error al cargar los datos iniciales', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchCuadrillas = async () => {
        const q = query(collection(firestore, 'cuadrillas_huawei'), orderBy('id', 'asc'));
        const querySnapshot = await getDocs(q);
        const cuadrillasData = [];
        querySnapshot.forEach((docSnap) => {
            cuadrillasData.push({ ...docSnap.data(), docId: docSnap.id });
        });
        setCuadrillas(cuadrillasData);
    };

    const fetchObras = async () => {
        const q = query(collection(firestore, 'obras_huawei'), orderBy('id', 'asc'));
        const querySnapshot = await getDocs(q);
        const obrasData = [];
        querySnapshot.forEach((docSnap) => {
            obrasData.push({ ...docSnap.data(), docId: docSnap.id });
        });
        setObras(obrasData);
    };

    const fetchMateriales = async () => {
        const q = query(collection(firestore, 'materiales_huawei'), orderBy('id', 'asc'));
        const querySnapshot = await getDocs(q);
        const materialesData = [];
        querySnapshot.forEach((docSnap) => {
            materialesData.push({ ...docSnap.data(), docId: docSnap.id });
        });
        setMateriales(materialesData);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (newDate) => {
        setFormData(prev => ({ ...prev, fecha: newDate }));
    };

    const handleMaterialSelect = (event, newValue) => {
        if (newValue && !materialesSeleccionados.find(m => m.id === newValue.id)) {
            setMaterialesSeleccionados([...materialesSeleccionados, {
                ...newValue,
                cantidad: 0,
                observaciones: ''
            }]);
        }
    };

    const handleMaterialChange = (index, field, value) => {
        const updated = [...materialesSeleccionados];
        updated[index][field] = value;
        setMaterialesSeleccionados(updated);
    };

    const handleRemoveMaterial = (index) => {
        setMaterialesSeleccionados(materialesSeleccionados.filter((_, i) => i !== index));
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const getDepositosDisponibles = () => {
        return cuadrillas.filter(c => c.tipo === 'deposito');
    };

    const getCuadrillasByObra = (obraId) => {
        // Aquí deberías filtrar las cuadrillas asociadas a la obra
        // Por ahora devuelvo los depósitos
        return cuadrillas.filter(c => c.tipo === 'deposito');
    };

    const validateForm = () => {
        if (!formData.fecha) {
            showSnackbar('Debe seleccionar una fecha', 'warning');
            return false;
        }

        if (formData.tipoRemito === 'Ingreso') {
            const cuadrillaOrigenFinal = isOtroCuadrillaOrigen ? cuadrillaOrigenCustom : formData.cuadrillaOrigen;
            if (!formData.cuadrillaDestino || !cuadrillaOrigenFinal) {
                showSnackbar('Debe seleccionar cuadrilla origen y destino', 'warning');
                return false;
            }
            if (!formData.obraDestino) {
                showSnackbar('Debe seleccionar la obra destino', 'warning');
                return false;
            }
        } else if (formData.tipoRemito === 'Egreso') {
            if (!formData.cuadrillaOrigen || !formData.cuadrillaDestino) {
                showSnackbar('Debe seleccionar cuadrilla origen e ingresar cuadrilla destino', 'warning');
                return false;
            }
            if (!formData.obraDestino) {
                showSnackbar('Debe seleccionar la obra destino', 'warning');
                return false;
            }
        } else {
            if (!formData.obraOrigen || !formData.obraDestino) {
                showSnackbar('Debe seleccionar obra origen y destino', 'warning');
                return false;
            }
            if (formData.obraOrigen === formData.obraDestino) {
                showSnackbar('La obra origen y destino no pueden ser la misma', 'warning');
                return false;
            }
            if (!formData.cuadrillaOrigen || !formData.cuadrillaDestino) {
                showSnackbar('Debe seleccionar cuadrilla origen y destino', 'warning');
                return false;
            }
        }

        if (materialesSeleccionados.length === 0) {
            showSnackbar('Debe seleccionar al menos un material', 'warning');
            return false;
        }

        for (let i = 0; i < materialesSeleccionados.length; i++) {
            const mat = materialesSeleccionados[i];
            if (!mat.cantidad || mat.cantidad <= 0) {
                showSnackbar(`Debe ingresar una cantidad válida para ${mat.descripcion}`, 'warning');
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);

            await runTransaction(firestore, async (transaction) => {
                const remitoRef = doc(collection(firestore, 'remitos_huawei'));
                const cuadrillaDestino = cuadrillas.find(c => c.docId === formData.cuadrillaDestino);
                const cuadrillaOrigen = cuadrillas.find(c => c.docId === formData.cuadrillaOrigen);
                const obraDestino = obras.find(o => o.docId === formData.obraDestino);
                const obraOrigen = obras.find(o => o.docId === formData.obraOrigen);

                // ——— FASE 1: TODAS LAS LECTURAS (Firestore exige reads antes que writes) ———
                const stockSnaps = [];
                const obraSnaps = {};

                if (formData.tipoRemito === 'Ingreso' && cuadrillaDestino) {
                    for (const material of materialesSeleccionados) {
                        const stockRef = doc(firestore, 'materialesxcuadrilla_huawei', `${cuadrillaDestino.id}_${material.id}`);
                        stockSnaps.push({ ref: stockRef, material, tipo: 'ingreso', cuadrilla: cuadrillaDestino });
                    }
                } else if (formData.tipoRemito === 'Egreso' && cuadrillaOrigen) {
                    for (const material of materialesSeleccionados) {
                        const stockRef = doc(firestore, 'materialesxcuadrilla_huawei', `${cuadrillaOrigen.id}_${material.id}`);
                        stockSnaps.push({ ref: stockRef, material, tipo: 'egreso' });
                    }
                } else if (formData.tipoRemito === 'Pase de materiales' && cuadrillaOrigen && cuadrillaDestino) {
                    for (const material of materialesSeleccionados) {
                        const stockOrigenRef = doc(firestore, 'materialesxcuadrilla_huawei', `${cuadrillaOrigen.id}_${material.id}`);
                        const stockDestinoRef = doc(firestore, 'materialesxcuadrilla_huawei', `${cuadrillaDestino.id}_${material.id}`);
                        stockSnaps.push({ ref: stockOrigenRef, material, tipo: 'pase-origen', cuadrillaOrigen, cuadrillaDestino });
                        stockSnaps.push({ ref: stockDestinoRef, material, tipo: 'pase-destino', cuadrillaOrigen, cuadrillaDestino });
                    }
                }

                // Lecturas de stock
                const stockResults = await Promise.all(stockSnaps.map(async (s) => ({
                    ...s,
                    snap: await transaction.get(s.ref)
                })));

                // Lecturas de obras (para cuadrillasAsociadas)
                if (formData.tipoRemito === 'Ingreso' && formData.obraDestino) {
                    const ref = doc(firestore, 'obras_huawei', formData.obraDestino);
                    obraSnaps.obraDestino = await transaction.get(ref);
                } else if (formData.tipoRemito === 'Egreso' && formData.obraDestino) {
                    const ref = doc(firestore, 'obras_huawei', formData.obraDestino);
                    obraSnaps.obraDestino = await transaction.get(ref);
                } else if (formData.tipoRemito === 'Pase de materiales') {
                    if (formData.obraOrigen) {
                        const ref = doc(firestore, 'obras_huawei', formData.obraOrigen);
                        obraSnaps.obraOrigen = await transaction.get(ref);
                    }
                    if (formData.obraDestino) {
                        const ref = doc(firestore, 'obras_huawei', formData.obraDestino);
                        obraSnaps.obraDestino = await transaction.get(ref);
                    }
                }

                // ——— FASE 2: CONSTRUIR remitoData Y VALIDAR CON DATOS LEÍDOS ———
                const remitoData = {
                    tipoRemito: formData.tipoRemito,
                    fecha: formData.fecha.format('DD/MM/YYYY'),
                    timestamp: formData.fecha.valueOf(),
                    observaciones: formData.observaciones,
                    materiales: materialesSeleccionados.map(m => ({
                        idMaterial: m.id,
                        codigo: m.codigo,
                        descripcion: m.descripcion,
                        unidad: m.unidad,
                        cantidad: parseFloat(m.cantidad),
                        observaciones: m.observaciones
                    })),
                    createdAt: moment().valueOf()
                };

                if (formData.tipoRemito === 'Ingreso') {
                    const cuadrillaOrigenFinal = isOtroCuadrillaOrigen ? cuadrillaOrigenCustom :
                        cuadrillas.find(c => c.docId === formData.cuadrillaOrigen)?.numero || '';
                    remitoData.cuadrillaOrigen = cuadrillaOrigenFinal;
                    remitoData.cuadrillaDestino = formData.cuadrillaDestino;
                    remitoData.obraDestino = formData.obraDestino;
                    remitoData.cuadrillaDestinoNombre = cuadrillaDestino?.numero;
                    remitoData.usuarioCuadrillaDestino = cuadrillaDestino?.usuario;
                    remitoData.obraDestinoNombre = obraDestino?.nombre;
                } else if (formData.tipoRemito === 'Egreso') {
                    remitoData.cuadrillaOrigen = formData.cuadrillaOrigen;
                    remitoData.cuadrillaOrigenNombre = cuadrillaOrigen?.numero;
                    remitoData.usuarioCuadrillaOrigen = cuadrillaOrigen?.usuario;
                    remitoData.cuadrillaDestino = formData.cuadrillaDestino;
                    remitoData.cuadrillaDestinoNombre = cuadrillaDestino?.numero;
                    remitoData.usuarioCuadrillaDestino = cuadrillaDestino?.usuario;
                    remitoData.obraDestino = formData.obraDestino;
                    remitoData.obraDestinoNombre = obraDestino?.nombre;
                } else {
                    remitoData.obraOrigen = formData.obraOrigen;
                    remitoData.obraOrigenNombre = obraOrigen?.nombre;
                    remitoData.obraDestino = formData.obraDestino;
                    remitoData.obraDestinoNombre = obraDestino?.nombre;
                    remitoData.cuadrillaOrigen = formData.cuadrillaOrigen;
                    remitoData.cuadrillaOrigenNombre = cuadrillaOrigen?.numero;
                    remitoData.usuarioCuadrillaOrigen = cuadrillaOrigen?.usuario;
                    remitoData.cuadrillaDestino = formData.cuadrillaDestino;
                    remitoData.cuadrillaDestinoNombre = cuadrillaDestino?.numero;
                    remitoData.usuarioCuadrillaDestino = cuadrillaDestino?.usuario;
                }

                // Validar stock suficiente (egreso / pase) usando resultados de lectura
                const paseOrigenByMaterial = new Map();
                const paseDestinoByMaterial = new Map();
                for (const r of stockResults) {
                    if (r.tipo === 'pase-origen') paseOrigenByMaterial.set(r.material.id, r);
                    if (r.tipo === 'pase-destino') paseDestinoByMaterial.set(r.material.id, r);
                }
                if (formData.tipoRemito === 'Egreso') {
                    for (const r of stockResults) {
                        if (r.tipo !== 'egreso') continue;
                        const current = r.snap.exists() ? r.snap.data().stock : 0;
                        if (current < parseFloat(r.material.cantidad)) {
                            throw new Error(`Stock insuficiente para ${r.material.descripcion} en cuadrilla origen`);
                        }
                    }
                } else if (formData.tipoRemito === 'Pase de materiales') {
                    for (const material of materialesSeleccionados) {
                        const rOrigen = paseOrigenByMaterial.get(material.id);
                        if (rOrigen && (!rOrigen.snap.exists() || rOrigen.snap.data().stock < parseFloat(material.cantidad))) {
                            throw new Error(`Stock insuficiente para ${material.descripcion} en cuadrilla origen`);
                        }
                    }
                }

                // ——— FASE 3: TODAS LAS ESCRITURAS ———
                if (formData.tipoRemito === 'Ingreso' && cuadrillaDestino) {
                    for (const r of stockResults) {
                        if (r.tipo !== 'ingreso') continue;
                        const currentStock = r.snap.exists() ? r.snap.data().stock : 0;
                        const newStock = currentStock + parseFloat(r.material.cantidad);
                        transaction.set(r.ref, {
                            idCuadrilla: r.cuadrilla.id,
                            idMaterial: r.material.id,
                            nombreCuadrilla: r.cuadrilla.numero,
                            codigoMaterial: r.material.codigo,
                            descripcionMaterial: r.material.descripcion,
                            stock: newStock,
                            ultimaActualizacion: moment().valueOf()
                        });
                    }
                } else if (formData.tipoRemito === 'Egreso' && cuadrillaOrigen) {
                    for (const r of stockResults) {
                        if (r.tipo !== 'egreso') continue;
                        const currentStock = r.snap.data().stock;
                        const newStock = currentStock - parseFloat(r.material.cantidad);
                        transaction.update(r.ref, {
                            stock: newStock,
                            ultimaActualizacion: moment().valueOf()
                        });
                    }
                } else if (formData.tipoRemito === 'Pase de materiales' && cuadrillaOrigen && cuadrillaDestino) {
                    for (const material of materialesSeleccionados) {
                        const rOrigen = paseOrigenByMaterial.get(material.id);
                        const rDestino = paseDestinoByMaterial.get(material.id);
                        const currentOrigen = rOrigen.snap.data().stock;
                        const newStockOrigen = currentOrigen - parseFloat(material.cantidad);
                        transaction.update(rOrigen.ref, {
                            stock: newStockOrigen,
                            ultimaActualizacion: moment().valueOf()
                        });
                        const currentDestino = rDestino.snap.exists() ? rDestino.snap.data().stock : 0;
                        const newStockDestino = currentDestino + parseFloat(material.cantidad);
                        transaction.set(rDestino.ref, {
                            idCuadrilla: cuadrillaDestino.id,
                            idMaterial: material.id,
                            nombreCuadrilla: cuadrillaDestino.numero,
                            codigoMaterial: material.codigo,
                            descripcionMaterial: material.descripcion,
                            stock: newStockDestino,
                            ultimaActualizacion: moment().valueOf()
                        });
                    }
                }

                if (formData.tipoRemito === 'Ingreso' && formData.obraDestino && obraSnaps.obraDestino?.exists()) {
                    const cuadrillaDestinoId = cuadrillas.find(c => c.docId === formData.cuadrillaDestino)?.id;
                    if (cuadrillaDestinoId) {
                        const obraDestinoRef = doc(firestore, 'obras_huawei', formData.obraDestino);
                        const cuadrillasActuales = obraSnaps.obraDestino.data().cuadrillasAsociadas || {};
                        transaction.update(obraDestinoRef, {
                            cuadrillasAsociadas: { ...cuadrillasActuales, [cuadrillaDestinoId]: true }
                        });
                    }
                } else if (formData.tipoRemito === 'Egreso' && formData.obraDestino && obraSnaps.obraDestino?.exists()) {
                    const cuadrillaOrigenId = cuadrillas.find(c => c.docId === formData.cuadrillaOrigen)?.id;
                    const cuadrillaDestinoId = cuadrillas.find(c => c.docId === formData.cuadrillaDestino)?.id;
                    const obraDestinoRef = doc(firestore, 'obras_huawei', formData.obraDestino);
                    const cuadrillasActuales = obraSnaps.obraDestino.data().cuadrillasAsociadas || {};
                    const nuevasAsociaciones = { ...cuadrillasActuales };
                    if (cuadrillaOrigenId) nuevasAsociaciones[cuadrillaOrigenId] = true;
                    if (cuadrillaDestinoId) nuevasAsociaciones[cuadrillaDestinoId] = true;
                    transaction.update(obraDestinoRef, { cuadrillasAsociadas: nuevasAsociaciones });
                } else if (formData.tipoRemito === 'Pase de materiales') {
                    const cuadrillaOrigenId = cuadrillas.find(c => c.docId === formData.cuadrillaOrigen)?.id;
                    const cuadrillaDestinoId = cuadrillas.find(c => c.docId === formData.cuadrillaDestino)?.id;
                    if (formData.obraOrigen && cuadrillaOrigenId && obraSnaps.obraOrigen?.exists()) {
                        const obraOrigenRef = doc(firestore, 'obras_huawei', formData.obraOrigen);
                        const cuadrillasOrigen = obraSnaps.obraOrigen.data().cuadrillasAsociadas || {};
                        transaction.update(obraOrigenRef, {
                            cuadrillasAsociadas: { ...cuadrillasOrigen, [cuadrillaOrigenId]: true }
                        });
                    }
                    if (formData.obraDestino && cuadrillaDestinoId && obraSnaps.obraDestino?.exists()) {
                        const obraDestinoRef = doc(firestore, 'obras_huawei', formData.obraDestino);
                        const cuadrillasDestino = obraSnaps.obraDestino.data().cuadrillasAsociadas || {};
                        transaction.update(obraDestinoRef, {
                            cuadrillasAsociadas: { ...cuadrillasDestino, [cuadrillaDestinoId]: true }
                        });
                    }
                }

                transaction.set(remitoRef, remitoData);
            });

            showSnackbar('Remito creado exitosamente', 'success');

            // Resetear formulario
            setFormData({
                tipoRemito: 'Ingreso',
                fecha: moment(),
                cuadrillaOrigen: '',
                cuadrillaDestino: '',
                obraOrigen: '',
                obraDestino: '',
                observaciones: ''
            });
            setIsOtroCuadrillaOrigen(false);
            setCuadrillaOrigenCustom('');
            setMaterialesSeleccionados([]);

        } catch (error) {
            console.error('Error al crear remito:', error);
            showSnackbar(error.message || 'Error al crear el remito', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (loading && cuadrillas.length === 0) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Nuevo Remito - Huawei
                </Typography>

                <Paper sx={{ p: 3, mt: 3 }}>
                    <Grid container spacing={3}>
                        {/* Tipo de Remito */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                select
                                label="Tipo de Remito *"
                                name="tipoRemito"
                                value={formData.tipoRemito}
                                onChange={handleInputChange}
                            >
                                {tiposRemito.map((tipo) => (
                                    <MenuItem key={tipo} value={tipo}>
                                        {tipo}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Fecha */}
                        <Grid item xs={12} md={6}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="Fecha *"
                                    value={formData.fecha}
                                    onChange={handleDateChange}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                    format="DD/MM/YYYY"
                                />
                            </LocalizationProvider>
                        </Grid>

                        {/* Campos según tipo de remito */}
                        {formData.tipoRemito === 'Ingreso' ? (
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Cuadrilla Origen *"
                                        name="cuadrillaOrigen"
                                        value={formData.cuadrillaOrigen}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === 'OTRO') {
                                                setIsOtroCuadrillaOrigen(true);
                                            } else {
                                                setIsOtroCuadrillaOrigen(false);
                                                setCuadrillaOrigenCustom('');
                                            }
                                            handleInputChange(e);
                                        }}
                                    >
                                        {getDepositosDisponibles().map((cuadrilla) => (
                                            <MenuItem key={cuadrilla.docId} value={cuadrilla.docId}>
                                                {cuadrilla.usuario ? `${cuadrilla.usuario} - ${cuadrilla.numero}` : cuadrilla.numero}
                                            </MenuItem>
                                        ))}
                                        <MenuItem value="OTRO">Otro</MenuItem>
                                    </TextField>
                                </Grid>
                                {isOtroCuadrillaOrigen && (
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Especificar Cuadrilla Origen *"
                                            value={cuadrillaOrigenCustom}
                                            onChange={(e) => setCuadrillaOrigenCustom(e.target.value)}
                                            placeholder="Nombre de la cuadrilla"
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Cuadrilla Destino (Depósito) *"
                                        name="cuadrillaDestino"
                                        value={formData.cuadrillaDestino}
                                        onChange={handleInputChange}
                                    >
                                        {getDepositosDisponibles().map((cuadrilla) => (
                                            <MenuItem key={cuadrilla.docId} value={cuadrilla.docId}>
                                                {cuadrilla.usuario ? `${cuadrilla.usuario} - ${cuadrilla.numero}` : cuadrilla.numero}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        options={obras}
                                        getOptionLabel={(option) => `${option.nombre} - ${option.localidad}`}
                                        value={obras.find(o => o.docId === formData.obraDestino) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, obraDestino: newValue?.docId || '' }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Obra Destino *"
                                                placeholder="Seleccionar obra..."
                                            />
                                        )}
                                    />
                                </Grid>
                            </>
                        ) : formData.tipoRemito === 'Egreso' ? (
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Cuadrilla Origen (Depósito) *"
                                        name="cuadrillaOrigen"
                                        value={formData.cuadrillaOrigen}
                                        onChange={handleInputChange}
                                    >
                                        {getDepositosDisponibles().map((cuadrilla) => (
                                            <MenuItem key={cuadrilla.docId} value={cuadrilla.docId}>
                                                {cuadrilla.usuario ? `${cuadrilla.usuario} - ${cuadrilla.numero}` : cuadrilla.numero}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        options={cuadrillas}
                                        getOptionLabel={(option) => `${option.usuario ? option.usuario + ' - ' : ''}${option.numero} (${option.tipo === 'deposito' ? 'Depósito' : 'Cuadrilla'})`}
                                        value={cuadrillas.find(c => c.docId === formData.cuadrillaDestino) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, cuadrillaDestino: newValue?.docId || '' }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Cuadrilla Destino *"
                                                placeholder="Seleccionar cuadrilla..."
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        options={obras}
                                        getOptionLabel={(option) => `${option.nombre} - ${option.localidad}`}
                                        value={obras.find(o => o.docId === formData.obraDestino) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, obraDestino: newValue?.docId || '' }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Obra Destino *"
                                                placeholder="Seleccionar obra..."
                                            />
                                        )}
                                    />
                                </Grid>
                            </>
                        ) : (
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Obra Origen *"
                                        name="obraOrigen"
                                        value={formData.obraOrigen}
                                        onChange={handleInputChange}
                                    >
                                        {obras.map((obra) => (
                                            <MenuItem key={obra.docId} value={obra.docId}>
                                                {obra.nombre} - {obra.localidad}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Obra Destino *"
                                        name="obraDestino"
                                        value={formData.obraDestino}
                                        onChange={handleInputChange}
                                    >
                                        {obras.map((obra) => (
                                            <MenuItem key={obra.docId} value={obra.docId}>
                                                {obra.nombre} - {obra.localidad}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        options={cuadrillas}
                                        getOptionLabel={(option) => `${option.usuario ? option.usuario + ' - ' : ''}${option.numero} (${option.tipo === 'deposito' ? 'Depósito' : 'Cuadrilla'})`}
                                        value={cuadrillas.find(c => c.docId === formData.cuadrillaOrigen) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, cuadrillaOrigen: newValue?.docId || '' }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Cuadrilla Origen *"
                                                placeholder="Seleccionar cuadrilla..."
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        options={cuadrillas}
                                        getOptionLabel={(option) => `${option.usuario ? option.usuario + ' - ' : ''}${option.numero} (${option.tipo === 'deposito' ? 'Depósito' : 'Cuadrilla'})`}
                                        value={cuadrillas.find(c => c.docId === formData.cuadrillaDestino) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, cuadrillaDestino: newValue?.docId || '' }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Cuadrilla Destino *"
                                                placeholder="Seleccionar cuadrilla..."
                                            />
                                        )}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Observaciones */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Observaciones"
                                name="observaciones"
                                value={formData.observaciones}
                                onChange={handleInputChange}
                            />
                        </Grid>

                        {/* Autocomplete de Materiales */}
                        <Grid item xs={12}>
                            <Autocomplete
                                options={materiales}
                                getOptionLabel={(option) => `${option.codigo} - ${option.descripcion}`}
                                onChange={handleMaterialSelect}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Seleccionar Material"
                                        placeholder="Buscar por código o descripción"
                                    />
                                )}
                            />
                        </Grid>

                        {/* Lista de materiales seleccionados */}
                        {materialesSeleccionados.length > 0 && (
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                    Materiales Seleccionados
                                </Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Unidad</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', width: 150 }}>Cantidad *</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Observaciones</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: 80 }}>Acción</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {materialesSeleccionados.map((material, index) => (
                                                <TableRow key={`${material.id}-${index}`}>
                                                    <TableCell>{material.codigo}</TableCell>
                                                    <TableCell>{material.descripcion}</TableCell>
                                                    <TableCell>
                                                        <Chip label={material.unidad} size="small" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={material.cantidad}
                                                            onChange={(e) => handleMaterialChange(index, 'cantidad', e.target.value)}
                                                            inputProps={{ min: 0, step: 1 }}
                                                            fullWidth
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            value={material.observaciones}
                                                            onChange={(e) => handleMaterialChange(index, 'observaciones', e.target.value)}
                                                            fullWidth
                                                            placeholder="Opcional"
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() => handleRemoveMaterial(index)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        )}

                        {/* Botones de acción */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => window.location.href = '/huawei/remitos'}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
                                >
                                    {loading ? 'Guardando...' : 'Crear Remito'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>

            {/* Snackbar para notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default AddRemito;
